import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import type { TrainingVideo } from "@xop/dashboard-shared";

import { fetchTrainingVideoBlob } from "./api.js";
import { previewForLink } from "./trainingLogic.js";

const STAGE_SX = {
  position: "relative" as const,
  width: "100%",
  aspectRatio: "16 / 9",
  borderRadius: 1,
  overflow: "hidden",
  bgcolor: "#000",
  border: 1,
  borderColor: "divider",
};

const FILL_SX = { position: "absolute" as const, inset: 0, width: "100%", height: "100%", border: 0 };

/**
 * What the topic's video looks like to the person watching it.
 *
 *   • a YouTube / Vimeo link → the provider's embed player in an iframe
 *     (both are in the desktop shell's CSP `frame-src`);
 *   • a direct video-file link → a native `<video controls>`;
 *   • an uploaded file → the bytes through the dashboard's staff relay
 *     (`preview_url` was rewritten server-side to that relay, so the signed
 *     portal-api address never reaches this page), fetched with the bearer
 *     and played from an object URL — a `<video src>` can't send one;
 *   • any other link → a button that opens it, since we can't frame it.
 */
export function TopicVideoPreview({ video, title }: { video: TrainingVideo; title: string }): JSX.Element {
  if (video.kind === "file") return <FilePreview relayPath={video.preview_url} contentType={video.content_type} />;

  const preview = previewForLink(video.url);
  if (preview.type === "iframe") {
    return (
      <Box sx={STAGE_SX} data-testid="topic-video-preview" data-preview="iframe">
        <Box
          component="iframe"
          title={title}
          src={preview.src}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={FILL_SX}
        />
      </Box>
    );
  }
  if (preview.type === "video") {
    return (
      <Box sx={STAGE_SX} data-testid="topic-video-preview" data-preview="video">
        <Box component="video" controls preload="metadata" src={preview.src} sx={{ ...FILL_SX, objectFit: "contain" }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{ ...STAGE_SX, display: "grid", placeItems: "center", bgcolor: "action.hover" }}
      data-testid="topic-video-preview"
      data-preview="external"
    >
      <Box sx={{ display: "grid", gap: 1, placeItems: "center", p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {preview.type === "invalid"
            ? "That doesn't look like a web address."
            : "This link can't be previewed here — resellers will be sent to it."}
        </Typography>
        {preview.type === "external" && (
          <Button size="small" variant="outlined" component="a" href={preview.href} target="_blank" rel="noopener noreferrer">
            Open the link ↗
          </Button>
        )}
      </Box>
    </Box>
  );
}

function FilePreview({ relayPath, contentType }: { relayPath: string; contentType: string }): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setSrc(null);
    setError(null);
    fetchTrainingVideoBlob(relayPath)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: contentType }));
        setSrc(url);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the video");
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [relayPath, contentType]);

  return (
    <Box sx={STAGE_SX} data-testid="topic-video-preview" data-preview="file">
      {src ? (
        <Box component="video" controls preload="metadata" src={src} sx={{ ...FILL_SX, objectFit: "contain" }} />
      ) : (
        <Box sx={{ ...FILL_SX, display: "grid", placeItems: "center" }}>
          {error ? (
            <Typography variant="body2" sx={{ color: "common.white", px: 2, textAlign: "center" }}>
              {error}
            </Typography>
          ) : (
            <CircularProgress size={28} sx={{ color: "common.white" }} />
          )}
        </Box>
      )}
    </Box>
  );
}
